import { View, type ViewStyle } from "react-native";
import { colors, radii, semanticColors } from "../theme/tokens";

/**
 * Bordered input chrome — the visual frame that every Phase 2.2 field
 * (numeric, currency, time, select, …) composes on top of.
 *
 * It does not own focus tracking. The composing field watches the inner
 * input's onFocus/onBlur and passes `focused` down. That keeps the chrome
 * a dumb visual primitive and avoids ref-forwarding or imperative APIs.
 *
 * State mapping (Phase 1.0 component matrix):
 *   - idle:     border  = semanticColors.border.subtle
 *   - focused:  border  = semanticColors.border.subtle
 *   - error:    border  = colors.coral (overrides focused)
 *   - disabled: parent  (typically MoonFormField) drops the wrapper to
 *               opacity 0.5; chrome stays in idle colours so it doesn't
 *               compete with the dim.
 *
 * Background stays at the existing modal-input value (rgba(255,255,255,0.5))
 * so swapping inline borders for this primitive is pixel-identical.
 */
type Props = {
  children: React.ReactNode;
  hasError?: boolean;
  focused?: boolean;
  focusedBorderColor?: string;
  /**
   * Horizontal padding around children. Defaults to 14 — matches the
   * inline pattern in care/modals so adoption is non-visual.
   */
  paddingX?: number;
  /**
   * Vertical padding. Defaults to 0 because most TextInput children pin
   * their own vertical padding (so the tap target stays close to the
   * border). Set this when the child is a Pressable or non-TextInput.
   */
  paddingY?: number;
  style?: ViewStyle;
};

export function MoonFieldInput({
  children,
  hasError,
  focused,
  focusedBorderColor = semanticColors.border.subtle,
  paddingX = 14,
  paddingY = 0,
  style,
}: Props) {
  const borderColor = hasError
    ? colors.coral
    : focused
      ? focusedBorderColor
      : semanticColors.border.subtle;

  return (
    <View
      className="font-dm-sans"
      style={[
        {
          borderRadius: radii.md,
          borderWidth: 1,
          borderColor,
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          paddingHorizontal: paddingX,
          paddingVertical: paddingY,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}
